/** Max stored size for partner signup uploads (images & PDFs). */
export const MAX_STORED_FILE_BYTES = 350_000;
export const MAX_CAMERA_FILE_BYTES = 120_000;
export const MAX_RAW_CAMERA_BYTES = 10 * 1024 * 1024;
export const MAX_CAMERA_PREVIEW_BYTES = 32_000;

export const CHOOSE_ANOTHER_FILE_MSG = 'Unable to reduce file size. Please choose another file.';

let compressQueue = Promise.resolve();

export function prefersMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

function isLowMemoryDevice() {
  if (prefersMobileDevice()) return true;
  const mem = navigator.deviceMemory;
  if (typeof mem === 'number' && mem <= 4) return true;
  return false;
}

function yieldToMain(ms = 0) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout(promise, ms, onTimeout) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      onTimeout?.();
      reject(new Error('timeout'));
    }, ms);
    Promise.resolve(promise).then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function releaseCanvas(canvas) {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
}

function toJpegFile(blob, originalName) {
  if (!blob) return null;
  const baseName = String(originalName || 'upload').replace(/\.[^.]+$/, '') || 'upload';
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

function scaledDimensions(width, height, maxDim) {
  const safeW = Math.max(1, Number(width) || 1);
  const safeH = Math.max(1, Number(height) || 1);
  const scale = Math.min(1, maxDim / Math.max(safeW, safeH));
  return {
    width: Math.max(1, Math.round(safeW * scale)),
    height: Math.max(1, Math.round(safeH * scale)),
  };
}

function bitmapToCanvas(bitmap, maxDim) {
  const { width: targetW, height: targetH } = scaledDimensions(bitmap.width, bitmap.height, maxDim);
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, targetW, targetH);
  return canvas;
}

async function canvasToJpegFile(canvas, originalName, quality) {
  const blob = await withTimeout(
    new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality)),
    8000
  );
  return toJpegFile(blob, originalName);
}

async function canvasToPreviewFile(canvas, originalName, previewMaxDim = 64) {
  const { width: targetW, height: targetH } = scaledDimensions(
    canvas.width,
    canvas.height,
    previewMaxDim
  );

  if (targetW === canvas.width && targetH === canvas.height) {
    return canvasToJpegFile(canvas, `preview-${originalName}`, 0.42);
  }

  const thumbCanvas = document.createElement('canvas');
  thumbCanvas.width = targetW;
  thumbCanvas.height = targetH;
  thumbCanvas.getContext('2d').drawImage(canvas, 0, 0, targetW, targetH);
  try {
    return canvasToJpegFile(thumbCanvas, `preview-${originalName}`, 0.42);
  } finally {
    releaseCanvas(thumbCanvas);
  }
}

async function decodeViaBitmap(file, maxDim) {
  if (typeof createImageBitmap !== 'function') return null;
  let bitmap = null;
  try {
    bitmap = await withTimeout(
      createImageBitmap(file, {
        resizeWidth: maxDim,
        resizeQuality: 'low',
        imageOrientation: 'from-image',
        premultiplyAlpha: 'none',
      }),
      10000,
      () => bitmap?.close?.()
    );
    const canvas = bitmapToCanvas(bitmap, maxDim);
    bitmap.close();
    bitmap = null;
    return canvas;
  } catch {
    bitmap?.close?.();
    return null;
  }
}

async function decodeViaImageElement(file, maxDim) {
  const url = URL.createObjectURL(file);
  try {
    const img = await withTimeout(
      new Promise((resolve, reject) => {
        const el = new Image();
        el.decoding = 'async';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('decode'));
        el.src = url;
      }),
      10000
    );
    const { width: targetW, height: targetH } = scaledDimensions(
      img.naturalWidth,
      img.naturalHeight,
      maxDim
    );
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    canvas.getContext('2d').drawImage(img, 0, 0, targetW, targetH);
    img.src = '';
    return canvas;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function decodeToCanvas(file, maxDim, { allowImageFallback = false } = {}) {
  const fromBitmap = await decodeViaBitmap(file, maxDim);
  if (fromBitmap) return fromBitmap;
  if (!allowImageFallback) return null;
  return decodeViaImageElement(file, maxDim);
}

async function encodeCanvasAtQualities(canvas, fileName, qualities, maxBytes) {
  for (const quality of qualities) {
    await yieldToMain(isLowMemoryDevice() ? 80 : 0);
    const prepared = await canvasToJpegFile(canvas, fileName, quality);
    if (prepared && prepared.size <= maxBytes) return prepared;
  }
  return null;
}

function isPdfFile(file) {
  const type = file?.type || '';
  return type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');
}

function inferMimeFromFileName(name = '') {
  if (/\.pdf$/i.test(name)) return 'application/pdf';
  if (/\.jpe?g$/i.test(name)) return 'image/jpeg';
  if (/\.png$/i.test(name)) return 'image/png';
  if (/\.webp$/i.test(name)) return 'image/webp';
  if (/\.gif$/i.test(name)) return 'image/gif';
  if (/\.heic$/i.test(name)) return 'image/heic';
  if (/\.heif$/i.test(name)) return 'image/heif';
  return '';
}

export function isImageFile(file) {
  if (!file) return false;
  const type = String(file.type || '');
  const name = String(file.name || '');
  return type.startsWith('image/')
    || /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(name);
}

/**
 * Phone cameras often return empty MIME and/or no extension — normalize before compress/upload.
 */
export function normalizeCaptureFile(file, { cameraOnly = false } = {}) {
  if (!file) return file;
  const inferred = inferMimeFromFileName(file.name);
  const mimeType = file.type || inferred || (cameraOnly || isImageFile(file) ? 'image/jpeg' : '');
  let fileName = String(file.name || '').trim() || (cameraOnly ? 'camera-capture.jpg' : 'upload');
  if ((cameraOnly || (mimeType && mimeType.startsWith('image/'))) && !/\.[^.]+$/.test(fileName)) {
    fileName = `${fileName}.jpg`;
  }
  if (file.type === mimeType && file.name === fileName) return file;
  return new File([file], fileName, { type: mimeType || 'application/octet-stream' });
}

async function compressImageFile(file, { cameraOnly = false } = {}) {
  const lowMemory = isLowMemoryDevice();
  const maxBytes = cameraOnly ? MAX_CAMERA_FILE_BYTES : MAX_STORED_FILE_BYTES;

  if (cameraOnly && lowMemory) {
    const { releaseAllSignupPreviews } = await import('./partnerSignupPreviewMemory');
    releaseAllSignupPreviews();
    await yieldToMain(300);
  }

  const dimSteps = cameraOnly
    ? (lowMemory ? [144] : [320, 256])
    : (lowMemory ? [320, 256] : [640, 480, 360]);
  const qualities = cameraOnly
    ? (lowMemory ? [0.30, 0.24] : [0.40, 0.32, 0.26])
    : (lowMemory ? [0.40, 0.32, 0.26] : [0.52, 0.42, 0.34, 0.28]);

  if (lowMemory) {
    await yieldToMain(200);
  }

  for (let i = 0; i < dimSteps.length; i += 1) {
    const maxDim = dimSteps[i];
    const allowImageFallback = lowMemory && i === dimSteps.length - 1;
    await yieldToMain(lowMemory ? 120 : 0);

    const canvas = await decodeToCanvas(file, maxDim, { allowImageFallback });
    if (!canvas) continue;

    try {
      const prepared = await encodeCanvasAtQualities(canvas, file.name, qualities, maxBytes);
      if (!prepared) continue;

      const previewFile = await canvasToPreviewFile(canvas, file.name, 64);
      return { file: prepared, previewFile };
    } finally {
      releaseCanvas(canvas);
    }
  }

  throw new Error('compress_failed');
}

/** @deprecated Prefer preview from prepareStoredUploadFile — avoids a second decode. */
export async function buildPreviewJpegFile(file, maxDim = 64, quality = 0.42) {
  if (!file || !isImageFile(file)) return null;
  await yieldToMain(80);
  const canvas = await decodeToCanvas(file, maxDim, { allowImageFallback: isLowMemoryDevice() });
  if (!canvas) return null;
  try {
    return canvasToJpegFile(canvas, `preview-${file.name}`, quality);
  } finally {
    releaseCanvas(canvas);
  }
}

async function prepareStoredUploadFileInner(file, options = {}) {
  if (!file) throw new Error('empty');

  const normalized = normalizeCaptureFile(file, options);

  if (isPdfFile(normalized)) {
    if (normalized.size > MAX_STORED_FILE_BYTES) throw new Error('too_large');
    return { file: normalized, previewFile: null };
  }

  if (!isImageFile(normalized)) throw new Error('unsupported');

  return compressImageFile(normalized, options);
}

export async function prepareStoredUploadFile(file, options = {}) {
  const run = () => prepareStoredUploadFileInner(file, options);
  const next = compressQueue.then(run, run);
  compressQueue = next.catch(() => {});
  return next;
}

/** Mobile camera: upload raw file to server — no canvas decode (prevents OOM on 2nd photo). */
export function shouldUseMobileCameraDirectUpload(cameraOnly = false) {
  return Boolean(cameraOnly && prefersMobileDevice());
}

/** Defer server upload for gallery picks on mobile; camera uploads go immediately. */
export function shouldDeferSignupUpload({ cameraOnly = false } = {}) {
  if (shouldUseMobileCameraDirectUpload(cameraOnly)) return false;
  return prefersMobileDevice();
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = typeof dataUrl === 'string' ? dataUrl.split(',')[1] : '';
      if (!base64) {
        reject(new Error('read'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('read'));
    reader.readAsDataURL(blob);
  });
}

/** Compress image (or pass PDF through) then upload to Cloudinary / server. */
export async function compressAndUploadFile(file, options = {}) {
  const prepared = await prepareStoredUploadFile(file, options);
  return uploadPreparedFile(prepared);
}

export async function uploadPreparedFile(prepared) {
  const file = prepared?.file || prepared;
  if (!file) throw new Error('empty');
  const { isCloudinaryDirectConfigured, uploadToCloudinary } = await import('../../utils/cloudinaryUpload');
  if (isCloudinaryDirectConfigured()) {
    return uploadToCloudinary(file);
  }
  const { authService } = await import('../../services/apiService');
  return authService.uploadFileBlob(file, { skipCompress: true });
}

/**
 * Mobile camera: upload the capture as-is (normalized MIME/name only).
 * Never canvas-decode on phone — the 2nd shop/street photo OOMs Android Chrome.
 * Backend accepts images up to 10MB.
 */
export async function prepareMobileCameraUpload(file) {
  const normalized = normalizeCaptureFile(file, { cameraOnly: true });
  if (!normalized || !normalized.size) throw new Error('empty');
  if (normalized.size > MAX_RAW_CAMERA_BYTES) throw new Error('too_large');
  // Yield so the camera UI / previous blob can be reclaimed before upload.
  await yieldToMain(isLowMemoryDevice() ? 400 : 80);
  return normalized;
}

/** Give the browser time to free camera/canvas memory between captures. */
export async function recoverCameraMemory(ms = 700) {
  releaseCanvas(null);
  if (typeof window !== 'undefined') {
    try {
      const { releaseAllSignupPreviews } = await import('./partnerSignupPreviewMemory');
      releaseAllSignupPreviews();
    } catch {
      // ignore
    }
  }
  await yieldToMain(ms);
  // Hint GC on engines that expose it (no-op elsewhere).
  try {
    if (typeof window !== 'undefined' && typeof window.gc === 'function') {
      window.gc();
    }
  } catch {
    // ignore
  }
}

export function getUploadErrorMessage(err) {
  const apiMsg = err?.response?.data?.message;
  if (apiMsg) return String(apiMsg);
  const reason = String(err?.message || err || '');
  if (/memory|quota|low memory/i.test(reason)) {
    return 'Device memory is low. Wait a few seconds, close other apps, then retake the photo.';
  }
  if (reason === 'too_large') {
    return 'Photo is too large. Move closer or retake at a lower resolution.';
  }
  if (reason === 'compress_failed' || reason === 'idb_unavailable' || reason === 'unsupported' || reason === 'empty') {
    return CHOOSE_ANOTHER_FILE_MSG;
  }
  if (/network|failed to fetch|timeout|Network Error/i.test(reason)) {
    return 'Upload failed. Check your connection and try again.';
  }
  if (/status code 4\d\d|status code 5\d\d/i.test(reason)) {
    return 'Upload failed. Please retake the photo and try again.';
  }
  return CHOOSE_ANOTHER_FILE_MSG;
}
